// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import { Link, Theme, createStyles, makeStyles } from "@material-ui/core";
import React, { useCallback, useState } from "react";
import { differenceInYears, parse, startOfToday } from "date-fns";

import Asset from "components/ui/Asset";
import DeclarativeList from "components/DeclarativeComponents/DeclarativeList";
import MarkDown from "components/ui/MarkDown";
import NextStepLink from "../NextStepLink";
import Title from "components/ui/Title";
import { fbt } from "fbt";
import useCenteredStyle from "style/useCenteredStyle";
import { useTestRunState } from "store/testrun";

enum InstructionType {
  CHILD = 1,
  ADULT = 2,
}

export const useSwabNoseStyle = makeStyles((theme: Theme) =>
  createStyles({
    switcherText: {
      fontSize: "16px",
      // Increase tap area
      paddingTop: "20px",
      marginTop: "-20px",
      paddingBottom: "20px",
      marginBottom: "-20px",
    },
    switcherContainer: {
      width: "300px",
      display: "inline-block",
      maxWidth: "100%",
      textAlign: "right",
    },
  })
);

const SwabNosePanel = () => {
  const testRunState = useTestRunState();
  const dob = testRunState.testRun?.profileDetails?.dob;
  const [selectedType, setSelectedType] = useState<InstructionType | undefined>(
    undefined
  );
  const classes = useCenteredStyle();
  const swabNoseClasses = useSwabNoseStyle();
  const onShowAdultInstructions = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    setSelectedType(InstructionType.ADULT);
  }, []);

  const onShowPedsInstructions = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    setSelectedType(InstructionType.CHILD);
  }, []);

  if (dob === undefined) {
    return null;
  }

  const dobDate = parse(dob, "MM/dd/yyyy", new Date());
  const age = differenceInYears(startOfToday(), dobDate);

  const showAdultNose =
    selectedType !== InstructionType.CHILD &&
    (selectedType === InstructionType.ADULT || age >= 11);

  const is11To14 = age >= 11 && age <= 14;
  const is8To14 = age >= 8 && age <= 14;
  const is8To10 = age >= 8 && age <= 10;

  const showChildText =
    age <= 7 ||
    (is8To10 && selectedType !== InstructionType.ADULT) ||
    (is11To14 && selectedType === InstructionType.CHILD);

  const showOlderChildText = age >= 15 || (is8To14 && !showChildText);

  return (
    <>
      <div className={classes.centeredContent}>
        <Asset
          height={250}
          width={300}
          type="local"
          alt={fbt(
            "Animated image of swabbing nose",
            "Healthpulse swab nose: image description"
          )}
          src={
            showAdultNose
              ? "healthpulse/adult_nose_swab.gif"
              : is8To14
              ? "healthpulse/Pediatric_NoseSwab_15scale.gif"
              : "healthpulse/pediatric_nose_swab.gif"
          }
        />
      </div>
      {is8To14 && (
        <div className={classes.centeredContent}>
          <div className={swabNoseClasses.switcherContainer}>
            {showChildText && (
              <Link
                component="button"
                variant="body2"
                onClick={onShowAdultInstructions}
                className={swabNoseClasses.switcherText}
              >
                <fbt desc="Healthpulse swab nose: link to switch instructions">
                  Switch to instructions for an older child
                </fbt>
              </Link>
            )}
            {showOlderChildText && (
              <Link
                component="button"
                variant="body2"
                onClick={onShowPedsInstructions}
                className={swabNoseClasses.switcherText}
              >
                <fbt desc="Healthpulse swab nose: link to switch instructions">
                  Switch to instructions for a younger child
                </fbt>
              </Link>
            )}
          </div>
        </div>
      )}
      <Title content={fbt("Swab nose", "Healthpulse swab nose: title")} />
      {age <= 14 && (
        <MarkDown
          source={`**${fbt(
            "Children under 18 years of age should be swabbed by an adult.",
            "Healthpulse swab nose: step detail"
          )}**`}
        />
      )}
      <DeclarativeList>
        <li>
          {showChildText && (
            <MarkDown
              source={fbt(
                `Gently insert the **entire** soft tip of the swab into one nostril until 
                  you feel a bit of resistance (less than half an inch into the nostril 
                  for younger children).`,
                "Healthpulse swab nose: step detail"
              )}
            />
          )}
          {showOlderChildText && (
            <MarkDown
              source={fbt(
                `Gently insert the **entire** soft tip of the swab into one nostril until
                  you feel a bit of resistance (less than one inch into the nostril).`,
                "Healthpulse swab nose: step detail"
              )}
            />
          )}
        </li>
        <li>
          <MarkDown
            source={fbt(
              `Using medium pressure, rub the swab slowly in a circular motion around the 
                inside wall of the nostril **four times**.`,
              "Healthpulse swab nose: step detail"
            )}
          />
        </li>
        <li>
          <MarkDown
            source={fbt(
              `The swab tip should be touching the inside wall of the nostril through 
                each rotation.`,
              "Healthpulse swab nose: step detail"
            )}
          />
        </li>
        <li>
          <MarkDown
            source={fbt(
              "Repeat the same process with the **same swab** in the other nostril.",
              "Healthpulse swab nose: step detail"
            )}
          />
        </li>
      </DeclarativeList>
      <NextStepLink />
      <br />
    </>
  );
};

export default SwabNosePanel;
